// Issue 26: every studio has a social-media analysis. Posts entered by hand, the publication
// timeline (Sofia months), and the list of ALL stored posts with their full text.
import { useEffect, useState } from 'react';
import { Badge, Empty, ErrorMsg, Loading, Section, Time, Unknown } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import type { PostItem, PostList, PostType, Timeline } from '../../lib/deepTypes';
import { useApi } from '../../lib/session';
import type { StudioDetail } from '../../lib/types';

const PLATFORMS = ['linkedin', 'instagram', 'facebook', 'x', 'other'];
const FORMATS = ['', 'article', 'image', 'video', 'other'];
const TEXT_MAX = 20000;
const BY_HAND = 'entered by hand';

export const POST_TYPE_NAME: Record<PostType, string> = {
  project_showcase: 'Project promotion',
  technical: 'Technical / making-of',
  internal_tool: 'Own tool or product',
  company_culture: 'Company culture / team',
  hiring: 'Hiring',
  news: 'News / awards / announcements',
  other: 'Other',
};

export function ManualPostForm({ studio, onSaved }: { studio: StudioDetail; onSaved: (msg: string) => void }) {
  const [platform, setPlatform] = useState('linkedin');
  const [url, setUrl] = useState('');
  const [date, setDate] = useState('');
  const [format, setFormat] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api(`/api/studios/${studio.id}/posts`, {
        method: 'POST',
        body: { platform, text, url: url.trim() || null, published_at: date || null, format: format || null },
      });
      setText('');
      setUrl('');
      setDate('');
      setFormat('');
      onSaved('Post saved (entered by hand).');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={submit} className="stack-s" aria-label="Add a post by hand" data-testid="manual-post-form">
      <div className="inline-form">
        <label className="small">
          Platform{' '}
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} aria-label="Post platform">
            {PLATFORMS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="post URL (optional)" aria-label="Post URL" inputMode="url" />
        <label className="small">
          Published{' '}
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Post date" />
        </label>
        <label className="small">
          Format{' '}
          <select value={format} onChange={(e) => setFormat(e.target.value)} aria-label="Post format">
            {FORMATS.map((f) => (
              <option key={f} value={f}>
                {f || 'not stated'}
              </option>
            ))}
          </select>
        </label>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        placeholder="Paste the full text of the post"
        aria-label="Post text"
        maxLength={TEXT_MAX}
        required
      />
      <div className="row gap center">
        <button type="submit" className="btn small" disabled={busy || !text.trim()}>
          Save post
        </button>
        <span className="muted small">
          {text.length} / {TEXT_MAX} characters
        </span>
      </div>
      <ErrorMsg>{error}</ErrorMsg>
    </form>
  );
}

// --- timeline --------------------------------------------------------------------------------

const COLLECTED = 'var(--accent)';
const MANUAL = 'var(--warn)';

export function TimelineChart({ studioId, version }: { studioId: string; version: number }) {
  const [months, setMonths] = useState(12);
  const tl = useApi<Timeline>(`/api/studios/${studioId}/social/timeline?months=${months}&v=${version}`);
  const data = tl.data;
  const max = Math.max(1, ...(data?.months ?? []).map((m) => m.total));
  const typed = (data?.months ?? []).filter((m) => Object.keys(m.types).length > 0);
  return (
    <Section
      title="Publication timeline"
      aside={
        <label className="small">
          Months{' '}
          <select value={months} onChange={(e) => setMonths(Number(e.target.value))} aria-label="Timeline months">
            {[6, 12, 24, 36].map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
      }
    >
      {tl.loading && !data ? (
        <Loading what="Loading the timeline" />
      ) : !data ? (
        <ErrorMsg>{tl.error}</ErrorMsg>
      ) : data.months.every((m) => m.total === 0) ? (
        <Empty>No dated post in these {months} months.</Empty>
      ) : (
        <div className="stack-s" data-testid="timeline">
          <div
            role="img"
            aria-label={`Posts per month: ${data.months.map((m) => `${m.month} ${m.total}`).join(', ')}`}
            style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 120, borderBottom: '1px solid var(--line-strong)', padding: '0 2px' }}
          >
            {data.months.map((m) => (
              <div
                key={m.month}
                data-month={m.month}
                data-total={m.total}
                title={`${m.month}: ${m.total} post(s), ${m.collected} collected, ${m.manual} entered by hand`}
                style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'stretch', height: '100%' }}
              >
                <span className="small num" style={{ textAlign: 'center', color: 'var(--muted)' }}>
                  {m.total || ''}
                </span>
                <div style={{ height: `${(m.manual / max) * 90}%`, background: MANUAL, minHeight: m.manual ? 2 : 0 }} />
                <div style={{ height: `${(m.collected / max) * 90}%`, background: COLLECTED, minHeight: m.collected ? 2 : 0 }} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 4, padding: '0 2px' }} aria-hidden="true">
            {data.months.map((m) => (
              <span key={m.month} className="small muted" style={{ flex: 1, minWidth: 0, textAlign: 'center', fontSize: 10, overflow: 'hidden' }}>
                {data.months.length > 12 ? m.month.slice(5) : m.month.slice(2)}
              </span>
            ))}
          </div>
          <p className="small row gap">
            <span>
              <span style={{ display: 'inline-block', width: 10, height: 10, background: COLLECTED, marginRight: 4 }} />
              collected by the app
            </span>
            <span>
              <span style={{ display: 'inline-block', width: 10, height: 10, background: MANUAL, marginRight: 4 }} />
              {BY_HAND}
            </span>
          </p>
          <details className="small">
            <summary>Numbers per month and channel</summary>
            <div className="table-wrap">
              <table className="table compact">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="num">Posts</th>
                    <th>By channel (collected / by hand)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.months
                    .filter((m) => m.total > 0)
                    .map((m) => (
                      <tr key={m.month} data-row-month={m.month}>
                        <td>{m.month}</td>
                        <td className="num">{m.total}</td>
                        <td>
                          {Object.entries(m.channels)
                            .map(([c, v]) => `${c} ${v.collected} / ${v.manual}`)
                            .join(', ')}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </details>
          {typed.length > 0 && (
            <div className="small" data-testid="timeline-types">
              <strong>Post types per month</strong> <span className="muted">({data.types_note})</span>
              <ul className="plain">
                {typed.map((m) => (
                  <li key={m.month}>
                    {m.month}:{' '}
                    {Object.entries(m.types)
                      .map(([t, n]) => `${POST_TYPE_NAME[t as PostType] ?? t} ${n}`)
                      .join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="muted small">
            Months are Europe/Sofia calendar months. {data.undated > 0 && `${data.undated} post(s) without a date are not on the timeline. `}
            {data.outside_range > 0 && `${data.outside_range} older post(s) are outside these ${months} months. `}
            Posts entered by hand are on the timeline but are not part of the posting-rate figures above: those count only what a feed run observed, with its coverage.
          </p>
        </div>
      )}
    </Section>
  );
}

// --- all posts -------------------------------------------------------------------------------

function PostRow({ p, onChanged }: { p: PostItem; onChanged: (msg: string) => void }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(p.text ?? '');
  const [date, setDate] = useState(p.published_at ? p.published_at.slice(0, 10) : '');
  const [err, setErr] = useState('');
  const full = p.text ?? '';
  const long = full.length > 280;

  async function save() {
    setErr('');
    try {
      await api(`/api/posts/${p.id}`, { method: 'PATCH', body: { text, published_at: date || null } });
      setEditing(false);
      onChanged('Post updated.');
    } catch (e) {
      setErr(errorText(e));
    }
  }
  async function remove() {
    if (!window.confirm('Delete this post that you entered by hand?')) return;
    setErr('');
    try {
      await api(`/api/posts/${p.id}`, { method: 'DELETE' });
      onChanged('Post deleted.');
    } catch (e) {
      setErr(errorText(e));
    }
  }
  return (
    <li className="run" data-testid="post-row" data-by-hand={p.entered_by_hand ? '1' : '0'}>
      <div className="row gap center wrap small">
        {p.published_at ? <Time iso={p.published_at} /> : <Unknown>no date</Unknown>}
        <Badge>{p.channel}</Badge>
        {p.entered_by_hand && <Badge tone="warn">{BY_HAND}</Badge>}
        {p.fetched_on_demand && <Badge tone="warn">fetched on demand</Badge>}
        {p.format && <span className="muted">{p.format}</span>}
        {p.type && <Badge tone="ok">{POST_TYPE_NAME[p.type] ?? p.type}</Badge>}
        {p.url && (
          <a href={p.url} target="_blank" rel="noreferrer noopener">
            open post
          </a>
        )}
      </div>
      {p.title && <p className="small">
        <strong>{p.title}</strong>
      </p>}
      {editing ? (
        <div className="stack-s">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} aria-label="Edit post text" maxLength={TEXT_MAX} />
          <label className="small">
            Published <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Edit post date" />
          </label>
          <div className="row gap">
            <button type="button" className="btn small" onClick={() => void save()}>
              Save
            </button>
            <button type="button" className="btn small ghost" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className={`small post-text${open ? '' : ' clamp2'}`} style={{ whiteSpace: 'pre-wrap' }} data-testid="post-text">
            {open || !long ? full : `${full.slice(0, 280)}…`}
          </p>
          <div className="row gap small">
            {long && (
              <button type="button" className="link small" onClick={() => setOpen(!open)} aria-expanded={open}>
                {open ? 'Collapse' : `Read the full text (${full.length} characters)`}
              </button>
            )}
            {p.entered_by_hand && (
              <>
                <button type="button" className="link small" onClick={() => setEditing(true)}>
                  Edit
                </button>
                <button type="button" className="link small" onClick={() => void remove()}>
                  Delete
                </button>
              </>
            )}
          </div>
        </>
      )}
      {p.type_quote && (
        <p className="muted small">
          Labelled {POST_TYPE_NAME[p.type as PostType] ?? p.type}: “{p.type_quote}”
        </p>
      )}
      {p.tone.length > 0 && (
        <p className="muted small">
          Tone (model interpretation): {p.tone.map((t) => `${t.trait} — “${t.quote}”`).join('; ')}
        </p>
      )}
      <ErrorMsg>{err}</ErrorMsg>
    </li>
  );
}

const PAGE = 25;

export function AllPosts({ studioId, version, onChanged }: { studioId: string; version: number; onChanged: (msg: string) => void }) {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [channel, setChannel] = useState('');
  const [offset, setOffset] = useState(0);
  // A search box that waits for a pause in typing instead of querying on every key.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setQuery(q);
      setOffset(0);
    }, 300);
    return () => window.clearTimeout(t);
  }, [q]);
  const params = new URLSearchParams({ limit: String(PAGE), offset: String(offset), v: String(version) });
  if (query.trim()) params.set('q', query.trim());
  if (channel) params.set('channel', channel);
  const list = useApi<PostList>(`/api/studios/${studioId}/social/posts?${params.toString()}`);
  const d = list.data;
  return (
    <Section title={<>All posts {d && <Badge>{d.total}</Badge>}</>}>
      <div className="inline-form">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the post texts" aria-label="Search posts" />
        <select
          value={channel}
          onChange={(e) => {
            setChannel(e.target.value);
            setOffset(0);
          }}
          aria-label="Channel filter"
        >
          <option value="">all channels</option>
          {Object.entries(d?.channels ?? {}).map(([c, n]) => (
            <option key={c} value={c}>
              {c} ({n})
            </option>
          ))}
        </select>
      </div>
      {list.loading && !d ? (
        <Loading what="Loading posts" />
      ) : !d ? (
        <ErrorMsg>{list.error}</ErrorMsg>
      ) : d.items.length === 0 ? (
        <Empty>{query || channel ? 'No post matches.' : 'No post stored yet. Enter one above, or collect a channel (Deep studios).'}</Empty>
      ) : (
        <>
          <ul className="runs" data-testid="all-posts">
            {d.items.map((p) => (
              <PostRow key={`${p.id}:${p.text?.length}`} p={p} onChanged={onChanged} />
            ))}
          </ul>
          <div className="row gap center small">
            <button type="button" className="btn small" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
              Newer
            </button>
            <span className="muted">
              {offset + 1}–{Math.min(offset + PAGE, d.total)} of {d.total}
            </span>
            <button type="button" className="btn small" disabled={offset + PAGE >= d.total} onClick={() => setOffset(offset + PAGE)}>
              Older
            </button>
          </div>
        </>
      )}
      <p className="muted small">
        Full texts as stored. A post type and tone quote appear when a social analysis labelled that post; they are the model&apos;s reading, with the quote that supports it.
      </p>
    </Section>
  );
}
